<template>
    <TransitionRoot
        appear
        :show="visible"
        as="template"
        @before-enter="onOpen"
        @after-enter="onOpened"
        @before-leave="onClose"
        @after-leave="onClosed"
    >
        <Dialog
            as="div"
            :class="['dialog-root', { 'dialog-no-modal': !modal }]"
            :style="rootStyle"
            @close="handleDialogClose"
        >
            <!-- 遮罩层 -->
            <TransitionChild
                v-if="modal"
                as="template"
                enter="dialog-enter"
                enter-from="dialog-enter-from"
                enter-to="dialog-enter-to"
                leave="dialog-leave"
                leave-from="dialog-leave-from"
                leave-to="dialog-leave-to"
            >
                <div
                    class="dialog-overlay"
                    :style="overlayStyle"
                    @click="handleOverlayClick"
                />
            </TransitionChild>

            <div
                class="dialog-container"
                :style="containerStyle"
            >
                <div :class="wrapperClass">
                    <TransitionChild
                        as="template"
                        enter="dialog-panel-enter"
                        enter-from="dialog-panel-enter-from"
                        enter-to="dialog-panel-enter-to"
                        leave="dialog-panel-leave"
                        leave-from="dialog-panel-leave-from"
                        leave-to="dialog-panel-leave-to"
                    >
                        <DialogPanel
                            :class="panelClass"
                            :style="panelStyle"
                        >
                            <!-- Header -->
                            <div
                                v-if="title || $slots.header"
                                :class="['dialog-header', { 'dialog-header-center': center }, headerClass]"
                                :style="headerStyle"
                            >
                                <slot name="header">
                                    <DialogTitle class="dialog-title">
                                        {{ title }}
                                    </DialogTitle>
                                </slot>
                                <div></div>
                            </div>
                            <div
                                v-if="closable"
                                class="dialog-close-btn"
                                :style="closeStyle"
                                @click="handleCloseClick"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                                    />
                                </svg>
                            </div>

                            <!-- Content -->
                            <div
                                v-if="!destroyOnClose || visible"
                                :class="['dialog-content', bodyClass]"
                                :style="bodyStyle"
                            >
                                <slot />
                            </div>

                            <!-- Footer -->
                            <div
                                v-if="$slots.footer"
                                :class="['dialog-footer', { 'dialog-footer-center': center }, footerClass]"
                            >
                                <slot name="footer" />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
    import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
    import { computed, onBeforeUnmount, ref, watch, type CSSProperties } from 'vue'

    defineOptions({ name: 's-dialog' })

    interface Props {
        // 基础
        title?: string
        width?: string | number
        size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'

        // 关闭控制
        closable?: boolean
        closeOnOverlay?: boolean
        closeOnPressEscape?: boolean
        beforeClose?: (done: () => void) => void

        // 显示控制
        modal?: boolean
        fullscreen?: boolean
        top?: string
        alignCenter?: boolean
        center?: boolean
        zIndex?: number

        // 行为
        lockScroll?: boolean
        destroyOnClose?: boolean
        openDelay?: number
        closeDelay?: number

        // 样式
        customClass?: string
        customStyle?: CSSProperties
        headerClass?: string
        headerStyle?: CSSProperties
        bodyClass?: string
        bodyStyle?: CSSProperties
        footerClass?: string
        closeStyle?: CSSProperties
    }

    const props = withDefaults(defineProps<Props>(), {
        size: 'md',
        closable: true,
        closeOnOverlay: true,
        closeOnPressEscape: true,
        modal: true,
        fullscreen: false,
        top: '15vh',
        alignCenter: true,
        center: false,
        lockScroll: true,
        destroyOnClose: false,
        openDelay: 0,
        closeDelay: 0,
    })

    const emit = defineEmits<{
        open: []
        opened: []
        close: []
        closed: []
    }>()

    const open = defineModel<boolean>('open', { default: false })

    // 内部可见状态（用于延迟）
    const visible = ref(false)

    // 计算根样式（z-index）
    const rootStyle = computed<CSSProperties>(() => {
        const style: CSSProperties = {}
        if (props.zIndex) {
            style.zIndex = props.zIndex
        }
        return style
    })

    // 计算遮罩样式
    const overlayStyle = computed<CSSProperties>(() => {
        if (props.zIndex) {
            return { zIndex: props.zIndex }
        }
        return {}
    })

    // 计算容器样式
    const containerStyle = computed<CSSProperties>(() => {
        const style: CSSProperties = {}
        if (props.zIndex) {
            style.zIndex = props.zIndex + 1
        }
        return style
    })

    // 计算 wrapper 类名
    const wrapperClass = computed(() => [
        'dialog-wrapper',
        {
            'dialog-wrapper-top': !props.alignCenter && !props.fullscreen,
            'dialog-wrapper-center': props.alignCenter && !props.fullscreen,
        },
    ])

    // 计算面板类名
    const panelClass = computed(() => [
        'dialog-panel',
        `dialog-size-${props.size}`,
        props.customClass,
        {
            'dialog-fullscreen': props.fullscreen,
        },
    ])

    // 计算面板样式
    const panelStyle = computed<CSSProperties>(() => {
        const style: CSSProperties = {}

        // 自定义宽度
        if (props.width && !props.fullscreen) {
            style.width = typeof props.width === 'number' ? `${props.width}px` : props.width
            style.maxWidth = 'none'
        }

        // 顶部距离（非居中模式）
        if (!props.alignCenter && !props.fullscreen) {
            style.marginTop = props.top
        }

        // 合并外部自定义样式
        if (props.customStyle) {
            Object.assign(style, props.customStyle)
        }

        return style
    })

    // 处理打开/关闭延迟
    let openTimer: ReturnType<typeof setTimeout> | null = null
    let closeTimer: ReturnType<typeof setTimeout> | null = null

    watch(open, val => {
        if (val) {
            // 打开
            if (openTimer) clearTimeout(openTimer)
            if (closeTimer) clearTimeout(closeTimer)

            if (props.openDelay > 0) {
                openTimer = setTimeout(() => {
                    visible.value = true
                }, props.openDelay)
            } else {
                visible.value = true
            }
        } else {
            // 关闭
            if (openTimer) clearTimeout(openTimer)
            if (closeTimer) clearTimeout(closeTimer)

            if (props.closeDelay > 0) {
                closeTimer = setTimeout(() => {
                    visible.value = false
                }, props.closeDelay)
            } else {
                visible.value = false
            }
        }
    })

    // 滚动锁定说明：
    // Headless UI Dialog 内置滚动锁定机制（preventScroll + adjustScrollbarPadding），
    // 会自动给 html 设置 overflow:hidden 并补偿 paddingRight，无需手动处理。
    // 当 lockScroll=false 时，弹窗打开后仍然可以滚动页面（Headless UI 的锁定总是启用的，
    // 此 prop 保留用于未来扩展）。

    // 关闭前处理
    function doClose() {
        open.value = false
    }

    function handleCloseClick() {
        if (props.beforeClose) {
            props.beforeClose(doClose)
        } else {
            doClose()
        }
    }

    function handleOverlayClick() {
        if (props.closeOnOverlay) {
            handleCloseClick()
        }
    }

    // Headless UI Dialog @close 事件：只处理 ESC，不处理遮罩点击
    // 遮罩点击由 dialog-overlay 的 @click 独立处理，两条路径互不干扰
    function handleDialogClose() {
        if (props.closeOnPressEscape) {
            handleCloseClick()
        }
    }

    // 事件触发
    function onOpen() {
        emit('open')
    }

    function onOpened() {
        emit('opened')
    }

    function onClose() {
        emit('close')
    }

    function onClosed() {
        emit('closed')
    }

    // 清理
    onBeforeUnmount(() => {
        if (openTimer) clearTimeout(openTimer)
        if (closeTimer) clearTimeout(closeTimer)
    })

    // 暴露方法
    defineExpose({
        /** 关闭弹窗 */
        handleClose: handleCloseClick,
    })
</script>

<style scoped lang="scss">
    // 颜色变量
    $gray-100: #f3f4f6;
    $gray-400: #9ca3af;
    $gray-500: #6b7280;
    $gray-600: #4b5563;
    $gray-900: #111827;
    $white: #fff;
    $black-50: rgb(0 0 0 / 50%);

    .dialog-root {
        position: relative;
        z-index: 1000;
    }

    .dialog-no-modal {
        pointer-events: none;

        .dialog-panel {
            position: relative;
            pointer-events: auto;
        }
    }

    .dialog-overlay {
        position: fixed;
        z-index: 1000;
        background-color: $black-50;
        inset: 0;
    }

    .dialog-container {
        position: fixed;
        z-index: 1001;
        overflow-y: auto;
        inset: 0;
    }

    .dialog-wrapper {
        display: flex;
        justify-content: center;
        min-height: 100%;
        padding: 1rem;
    }

    .dialog-wrapper-center {
        align-items: center;
    }

    .dialog-wrapper-top {
        align-items: flex-start;
    }

    .dialog-panel {
        position: relative;
        width: 100%;
        background-color: $white;
        box-shadow:
            0 25px 50px -12px rgb(0 0 0 / 25%),
            0 0 15px rgb(0 0 0 / 10%);
        overflow: hidden;
        transition: all 0.2s ease-out;
        transform: scale(1);
    }

    // 全屏模式
    .dialog-fullscreen {
        width: 100vw !important;
        max-width: none !important;
        height: 100vh !important;
        margin: 0 !important;
    }

    // 尺寸
    .dialog-size-sm {
        max-width: 24rem;
    }

    .dialog-size-md {
        max-width: 28rem;
    }

    .dialog-size-lg {
        max-width: 32rem;
    }

    .dialog-size-xl {
        max-width: 36rem;
    }

    .dialog-size-full {
        max-width: 56rem;
    }

    .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1rem;
    }

    .dialog-header-center {
        justify-content: center;
        text-align: center;
    }

    .dialog-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: $gray-900;
    }

    .dialog-close-btn {
        position: absolute;
        top: 1rem;
        right: 1rem;
        display: flex;
        flex-shrink: 0;
        justify-content: center;
        align-items: center;
        padding: 0.25rem;
        border: none;
        border-radius: 0.5rem;
        color: $gray-400;
        background: transparent;
        transition: all 0.15s ease;
        cursor: pointer;

        &:hover {
            color: $gray-500;
            background-color: $gray-100;
        }
    }

    .dialog-content {
        color: $gray-600;
    }

    .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        margin-top: 1.5rem;
    }

    .dialog-footer-center {
        justify-content: center;
    }

    // 过渡动画 - Overlay
    .dialog-enter,
    .dialog-leave {
        transition: opacity 0.2s ease;
    }

    .dialog-enter-from,
    .dialog-leave-to {
        opacity: 0;
    }

    .dialog-enter-to,
    .dialog-leave-from {
        opacity: 1;
    }

    // 过渡动画 - Panel
    .dialog-panel-enter,
    .dialog-panel-leave {
        transition: all 0.2s ease;
    }

    .dialog-panel-enter-from,
    .dialog-panel-leave-to {
        opacity: 0;
        transform: scale(0.95);
    }

    .dialog-panel-enter-to,
    .dialog-panel-leave-from {
        opacity: 1;
        transform: scale(1);
    }
</style>
